import { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react'
import ReactDataSheet from 'react-datasheet'
import { Checkbox, Input } from '@progress/kendo-react-inputs'

import { SupplierMaxCapacity } from '@gateway.Supplier'
import { CellValue, GridElement, M2GridDataState } from './SufficiencyOfQuantity'
import { CodeName } from '../../../services/initializing/CodeName'

import 'react-datasheet/lib/react-datasheet.css'

import hearingMessage from '../../../data/hearing-message.json'
import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'
import { Tooltip } from '@progress/kendo-react-tooltip'

const SheetRenderer: FC<
PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
}>
> = ({ className, simulationYm, years, months, children, displayStartYear }) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.supplierMaxCapacityLocation)
  const locationTop = useAppSelector((state) => state.confirmreply.locationTop.ourCompanyCapacityLocation)
  const dispatch = useAppDispatch()

  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })

    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location, top: locationTop })
  }, [location, locationTop])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      style={{ maxHeight: '61vh' }}
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setSupplierMaxCapacityLocation(Number(ref.current?.scrollLeft)))
        dispatch(ConfirmReplyActions.setOurCompanyCapacityLocationTop(Number(ref.current?.scrollTop)))
      }}
    >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 40 }}>削除</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 76 }}>
              <div style={{ width: 80 }}>管轄支店</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 159 }}>
              <div style={{ width: 270 }}>対応可能エリア(その他)</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

const RowRenderer: FC<
PropsWithChildren<{
  deleteSelected: boolean
  nonCtrlFlg: boolean
  rowNo: number
  rowLength: number
  supplierMaxCapacity: SupplierMaxCapacity
  newAddFlg: boolean
  branches: CodeName[]
  onRemarkInputChange: (rowNo: number, val: string) => void
}>
> = ({
  deleteSelected,
  rowNo,
  rowLength,
  supplierMaxCapacity,
  nonCtrlFlg,
  newAddFlg,
  branches,
  // right part
  children,
  onRemarkInputChange
}) => {
  /* データ変換 */
  const branchesName = (DATA: string) => {
    return branches.find((item) => item.CODE === DATA)?.NAME
  }

  const isLastRow = rowNo === rowLength - 1

  if (isLastRow) {
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={4}
        >
          <span>合計</span>
        </th>
        {children}
      </tr>
    )
  }

  return (
    <tr style={{ height: 33 }}>
      <th style={{ position: 'sticky', left: 0 }}>
        <span>{rowNo + 1}</span>
      </th>
      <th style={{ position: 'sticky', left: 33 }}>
        <Checkbox disabled={nonCtrlFlg} checked={deleteSelected} />
      </th>
      <th style={{ position: 'sticky', left: 76 }}>
        <span>{branchesName(supplierMaxCapacity.branchCd)}</span>
      </th>
      {newAddFlg
        ? (
        <>
          <th style={{ position: 'sticky', left: 159 }}>
            <Input
              defaultValue={supplierMaxCapacity.remark ?? ''}
              onBlur={(e) => {
                onRemarkInputChange(rowNo, e.target.value)
              }}
            />
          </th>
        </>
          )
        : (
        <>
          <th style={{ position: 'sticky', left: 159 }}>
            <Tooltip position='right'>
              <span title={supplierMaxCapacity.remark ?? ''} style={{ width: 270, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                <Input
                  defaultValue={supplierMaxCapacity.remark ?? ''}
                  disabled={nonCtrlFlg}
                  onBlur={(e) => {
                    onRemarkInputChange(rowNo, e.target.value)
                  }}
                />
              </span>
            </Tooltip>
          </th>
        </>
          )}
      {children}
    </tr>
  )
}

// セルの設定
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  isRed: boolean
}> = ({ valueViewerProps, rowLength, isRed }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1

  return (
    <div
      style={{
        backgroundColor: isLastRow ? '#99FF99' : '',
        height: '100%',
        color: isRed ? '#F00' : ''
      }}
    >
      {valueViewerProps.value}
    </div>
  )
}

interface DispatchQuantityGridProps {
  gridDataState: M2GridDataState
  years: number
  months: number[]
  simulationYm: string
  branches: CodeName[]
  nonCtrlFlg: boolean
  onCellsChanged: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>,
    arrayOfAdditions?: ReactDataSheet.CellAdditionsArgs<CellValue>
  ) => void
  onRemarkInputChange: (rowNo: number, val: string) => void
  expenseCd: string
  displayStartYear: string
}

export const DispatchQuantityGrid: FC<DispatchQuantityGridProps> = (props) => {
  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8
  /** Table Header */
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  /** Table Row[Lift + Right(→rowRendererProps.children)] */
  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean }
  ) => {
    const supplierMaxCapacity: SupplierMaxCapacity =
      props.gridDataState.ourCompanyCapacityList[rowRendererProps.row]
    const currentRowDeleteSelection =
      supplierMaxCapacity !== undefined
        ? supplierMaxCapacity.delFlg !== 0
        : false
    const newAddFlg = props.gridDataState.newAddFlgs[rowRendererProps.row]

    return (
      <RowRenderer
        deleteSelected={currentRowDeleteSelection}
        nonCtrlFlg={props.nonCtrlFlg}
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        supplierMaxCapacity={supplierMaxCapacity}
        newAddFlg={newAddFlg}
        branches={props.branches}
        onRemarkInputChange={props.onRemarkInputChange}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    let isRed = false
    const rowNo = valueViewerProps.row
    const colNo = valueViewerProps.col
    const ourCompanyCapacityListLastMonth =
      props.gridDataState.ourCompanyCapacityListLastMonth

    // ※対象の支店コードに一致する変数．前月度労務能力．入場可能数．月別労務数が取得出来る場合
    if (
      ourCompanyCapacityListLastMonth.length > 0 &&
      props.gridDataState.ourCompanyCapacityList[rowNo] !== undefined &&
      ourCompanyCapacityListLastMonth.find((obj) => obj.branchCd ===
        props.gridDataState.ourCompanyCapacityList[rowNo].branchCd) !== undefined
    ) {
      const lastMonth = ourCompanyCapacityListLastMonth.find((obj) => obj.branchCd ===
        props.gridDataState.ourCompanyCapacityList[rowNo].branchCd)
      const simulatedMonth = Number(
        props.simulationYm.substring(4, props.simulationYm.length)
      )

      // ※データストア．業者ヒアリング遷移パラメータ．パラメータ．シミュレーション年月度が4月度の場合、
      // 変数．前月度労務能力．入場可能数．月別労務数のIndexを+12する
      if (simulatedMonth === 4) {
        if (colNo + 12 <= props.gridDataState.ourCompanyCapacityList[rowNo].workQuantityByMonth.length - 1) {
          isRed =
            props.gridDataState.ourCompanyCapacityList[rowNo]
              .workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo + 12]
        }
      } else {
        isRed =
          props.gridDataState.ourCompanyCapacityList[rowNo]
            .workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo]
      }
    } else {
      // ※対象の支店コードに一致する変数．前月度労務能力．入場可能数．月別労務数が取得出来ない場合（当月に追加された行）
      // 合計行は赤字の対象外とする
      if (rowNo !== props.gridDataState.ourCompanyCapacityList.length) {
        isRed = true
      }
    }

    return (
      <ValueViewer
        isRed={isRed}
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
      />
    )
  }

  return (
    <div style={{ position: 'sticky', zIndex: 2 }}>
      <h3 style={{ marginBottom: 0 }}>
        貴社対応可能数（大成）（単位：{unit}）
        <span
            style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
          >
          {hearingMessage.sufficiencyOfQuantity.dispatchQuantityAnnotationInfo}
        </span>
      </h3>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        onCellsChanged={props.onCellsChanged}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
      />
    </div>
  )
}
